import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import useAppStore from '../../store';
import { exchangeOauth2Code } from '../../lib/api/auth';
import { destinationForSession } from './postLogin';

/**
 * Google OAuth2 landing page — REAL PocketBase exchange (auth-full-spec §4).
 *
 * The provider lands here via the redirect_uri the app appended to authURL:
 * /#/sso/google?code=...&state=... (hash router: the code/state arrive inside
 * the hash). Flow:
 *   1. state must match the sessionStorage 'agiOps:oauth.pending' record
 *      (CSRF gate; mismatch/expired → error card, no exchange attempted);
 *   2. POST /api/collections/users/auth-with-oauth2 {provider, code,
 *      codeVerifier, redirectURL} via exchangeOauth2Code;
 *   3. UNPROVISIONED GATE (decision LOCKED): a session whose role is missing/
 *      blank/unrecognized maps to 'unprovisioned' — the token is NEVER applied
 *      or persisted; the person is routed to the request-only register flow.
 *      Provisioned sessions apply and navigate like password/OTP logins.
 */
function SSOCallbackPage() {
  const navigate = useNavigate();
  const { provider } = useParams();
  const [searchParams] = useSearchParams();
  const locationRef = useRef({ state: { from: { pathname: '/approvals' } } });
  const applySession = useAppStore((state) => state.applySession);
  const [error, setError] = useState('');
  const [unprovisioned, setUnprovisioned] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const oauthError = searchParams.get('error');

    if (oauthError) {
      setError(
        oauthError === 'access_denied'
          ? 'Google sign-in was cancelled. Close this and choose a different sign-in method.'
          : `Google returned an error (${oauthError}). Start the sign-in again.`
      );
      return undefined;
    }

    if (!code || !state) {
      setError('This sign-in link is missing its authorization code. Start the Google sign-in again.');
      return undefined;
    }

    exchangeOauth2Code({ provider, code, state })
      .then((result) => {
        if (cancelled) return;
        if (result.session.role === 'unprovisioned') {
          // Unprovisioned gate: no applySession — the PB token never touches
          // the store or storage; the person is routed to the request flow.
          setUnprovisioned(result.session.user);
          return;
        }
        applySession(result);
        navigate(destinationForSession(result.session, locationRef.current), { replace: true });
      })
      .catch((requestError) => {
        if (!cancelled) setError(requestError.message || 'Sign-in with Google failed.');
      });
    return () => {
      cancelled = true;
    };
  }, [provider, searchParams, applySession, navigate]);

  if (unprovisioned) {
    return (
      <main className="auth-screen">
        <div className="auth-card">
          <p className="eyebrow">ACCESS PENDING</p>
          <h1>Access pending</h1>
          <p className="auth-subtitle">
            Your Google account isn't provisioned for this platform. Use Create
            an account to request access, or contact the agency.
          </p>
          <div className="auth-links">
            <Link to="/register">Create an account</Link>
            <Link to="/login">Back to sign in</Link>
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="auth-screen">
        <div className="auth-card">
          <p className="eyebrow">SSO CALLBACK</p>
          <h1>Sign-in failed</h1>
          <div className="auth-error" role="alert">{error}</div>
          <div className="auth-links">
            <Link to="/forgot-password">Use password reset</Link>
            <Link to="/login">Back to sign in</Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="auth-screen">
      <div className="auth-card auth-card-center">
        <p className="eyebrow">SSO CALLBACK</p>
        <h1>Completing sign-in…</h1>
        <p className="auth-subtitle">
          Exchanging your Google authorization with the sign-in service. This takes a moment.
        </p>
        <div className="auth-spinner" aria-label="Signing in" role="status" />
      </div>
    </main>
  );
}

export default SSOCallbackPage;
